import React, { useEffect, useState } from "react";
import "./AdminRecord.css";
import {ThreeDot } from "react-loading-indicators";
import { motion } from "framer-motion";

// ---------- Utilities ----------
const normalizeName = (name) =>
  String(name || "")
    .replace(/\s+/g, " ")
    .replace(/\u00A0/g, " ")
    .trim();

const toArray = (x) =>
  Array.isArray(x) ? x : x?.data && Array.isArray(x.data) ? x.data : [];

function parseTime(str) {
  if (!str) return null;
  const s = String(str).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)?$/);
  if (!m) return null;
  let hour = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = parseInt(m[3] || "0", 10);
  const mer = m[4] ? m[4].toUpperCase() : null;
  if (mer === "PM" && hour < 12) hour += 12;
  if (mer === "AM" && hour === 12) hour = 0;
  return { hour, min, sec };
}

function minutesBetween(checkIn, checkOut) {
  const t1 = parseTime(checkIn),
    t2 = parseTime(checkOut);
  if (!t1 || !t2) return null;
  const start = new Date(0, 0, 0, t1.hour, t1.min, t1.sec);
  const end = new Date(0, 0, 0, t2.hour, t2.min, t2.sec);
  let diffMin = (end - start) / 60000;
  if (diffMin < 0) diffMin += 24 * 60;
  return diffMin;
}

function calculateDuration(checkIn, checkOut) {
  const mins = minutesBetween(checkIn, checkOut);
  if (mins == null) return "-";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}

const MONTH_OPTIONS = [
  { value: "", label: "--All Months--" },
  { value: 0, label: "Jan" },
  { value: 1, label: "Feb" },
  { value: 2, label: "Mar" },
  { value: 3, label: "Apr" },
  { value: 4, label: "May" },
  { value: 5, label: "Jun" },
  { value: 6, label: "Jul" },
  { value: 7, label: "Aug" },
  { value: 8, label: "Sep" },
  { value: 9, label: "Oct" },
  { value: 10, label: "Nov" },
  { value: 11, label: "Dec" }
];
const STATUS_OPTIONS = [
  { value: "Present", label: "Present" },
  { value: "Half Day", label: "Half Day" },
  { value: "Absent", label: "Absent" }
];

// ---------- Main Component ----------
function AdminAttendance() {
  const [attendanceList, setAttendanceList] = useState([]);
  const [leaveListRaw, setLeaveListRaw] = useState([]);
  const [employeesList, setEmployeesList] = useState([]);
  const [employeeNames, setEmployeeNames] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [monthBadges, setMonthBadges] = useState({
    checkouts: 0,
    short: 0,
    nocheckout: 0,
    noshow: 0,
    present: 0
  });
  const [editingIdx, setEditingIdx] = useState(null);
  const [editCheckIn, setEditCheckIn] = useState("");
  const [editCheckOut, setEditCheckOut] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);

  // Load attendance + employees + all leave on mount or refresh
  function loadData() {
    setLoading(true);
    Promise.all([
      fetch("https://hrms.urbango.co.in/server/index.php?endpoint=attendance").then((r) => r.json()),
      fetch("https://hrms.urbango.co.in/server/index.php?endpoint=employees").then((r) => r.json()),
      fetch("https://hrms.urbango.co.in/server/index.php?endpoint=leave").then((r) => r.json())
    ])
      .then(([attendance, employees, leaveList]) => {
        const att = toArray(attendance);
        setAttendanceList(att);
        setEmployeesList(toArray(employees));
        // Build unique employee names from attendance
        const seenNames = new Set();
        att.forEach((r) => {
          const n = normalizeName(r.employeeName || r.employee_name || r.name);
          if (n !== "") seenNames.add(n);
        });
        // Map back to display names for filtering
        const rawDisplayNames = {};
        att.forEach((r) => {
          const raw = (r.employeeName || r.employee_name || r.name || "").replace(/\s+/g, " ").trim();
          const norm = normalizeName(raw);
          if (norm && !(norm in rawDisplayNames)) rawDisplayNames[norm] = raw;
        });
        setEmployeeNames([...seenNames].map(n => rawDisplayNames[n]));
        setLeaveListRaw(toArray(leaveList));
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load records.");
        setLoading(false);
      });
  }

  useEffect(() => { loadData(); }, [reload]);

  // Filter attendance records as per selected filters
  const filteredAttendance = attendanceList.filter((r) => {
    const attNameRaw = (r.employeeName || r.employee_name || r.name || "").replace(/\s+/g, " ").trim();
    const attNameNorm = normalizeName(attNameRaw);
    const selectedNorm = normalizeName(selectedEmployee);
    const passEmployee = !selectedEmployee || (attNameNorm === selectedNorm);
    if (!passEmployee) return false;
    // Month/year filter
    if (selectedMonth === "" && selectedYear === "") return true;
    let d = null;
    if (r.date) {
      const parsed = new Date(r.date);
      if (!isNaN(parsed)) d = parsed;
    }
    if (d) {
      const m = d.getMonth(), y = d.getFullYear();
      const passMonth = selectedMonth === "" ? true : m === Number(selectedMonth);
      const passYear = selectedYear === "" ? true : y === Number(selectedYear);
      return passMonth && passYear;
    }
    return false;
  });

  // Filter leave records for selected employee
  const filteredLeave = leaveListRaw.filter((leave) => {
    if (!selectedEmployee) return false;
    const n = normalizeName(leave.employeeName || leave.employee_name || leave.name);
    return n === normalizeName(selectedEmployee);
  });

  // Compute badge counts based on selected filters
  useEffect(() => {
    if (!selectedEmployee) {
      setMonthBadges({
        checkouts: 0, short: 0, nocheckout: 0, noshow: 0, present: 0
      });
      return;
    }
    const targetMonth = selectedMonth === "" ? new Date().getMonth() : Number(selectedMonth);
    const targetYear = Number(selectedYear) || new Date().getFullYear();
    const start = new Date(targetYear, targetMonth, 1);
    const end = new Date(targetYear, targetMonth + 1, 0);
    end.setHours(0, 0, 0, 0);

    const workDays = [];
    const d = new Date(start);
    while (d <= end) {
      const w = d.getDay();
      if (w !== 0) workDays.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }

    const keyOf = (dt) =>
      dt.toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric"
      });

    const byDate = new Map();
    filteredAttendance.forEach((r) => {
      const rd = r.date ? new Date(r.date) : null;
      if (!rd || isNaN(rd)) return;
      if (rd.getMonth() !== targetMonth || rd.getFullYear() !== targetYear) return;
      byDate.set(keyOf(rd), r);
    });

    let checkouts = 0, short = 0, nocheckout = 0, noshow = 0, present = 0;
    workDays.forEach((dt) => {
      const k = keyOf(dt);
      const rec = byDate.get(k);
      if (!rec) {
        noshow += 1;
        return;
      }
      const hasIn = !!rec.checkIn;
      const hasOut = !!rec.checkOut;
      if (hasIn && hasOut) {
        checkouts += 1; present += 1;
        const mins = minutesBetween(rec.checkIn, rec.checkOut);
        if (mins != null) {
          const target = dt.getDay() === 6 ? 6 * 60 : 9 * 60;
          if (mins + 1e-6 < target) short += 1;
        }
        return;
      }
      if (hasIn && !hasOut) {
        nocheckout += 1; present += 1; return;
      }
    });
    setMonthBadges({ checkouts, short, nocheckout, noshow, present });
  }, [filteredAttendance, selectedEmployee, selectedMonth, selectedYear]);

  // Edit/save logic
  const handleEdit = (idx, rec) => {
    setEditingIdx(idx);
    setEditCheckIn(rec.checkIn || "");
    setEditCheckOut(rec.checkOut || "");
    setEditStatus(rec.status || "Present");
  };

  const handleSave = (idx, rec) => {
    setSaving(true);
    const updated = {
      ...rec,
      checkIn: editCheckIn,
      checkOut: editCheckOut,
      status: editStatus,
      duration: calculateDuration(editCheckIn, editCheckOut)
    };
    fetch("https://hrms.urbango.co.in/server/index.php?endpoint=update_attendance", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updated)
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          setEditingIdx(null);

          // Live update: update attendanceList with the updated record immediately
          setAttendanceList((prev) =>
            prev.map((r, i) =>
              i === idx ? { ...r, ...updated } : r
            )
          );
        } else {
          setError("Failed to update attendance: " + (result.error || ""));
        }
        setSaving(false);
      })
      .catch(() => {
        setError("Failed to update attendance");
        setSaving(false);
      });
  };

  // Delete
  const handleDelete = (idx, rec) => {
    if (!window.confirm("Are you sure you want to delete this attendance record?")) return;
    fetch("https://hrms.urbango.co.in/server/index.php?endpoint=delete_attendance", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        date: rec.date,
        employeeName: rec.employeeName
      })
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          setAttendanceList((prev) =>
            prev.filter((item, i) =>
              !(item.date === rec.date && item.employeeName === rec.employeeName && i === idx)
            )
          );
        } else {
          setError("Failed to delete attendance: " + (result.message || ""));
        }
      })
      .catch(() => {
        setError("Failed to delete attendance");
      });
  };

  // CSV download generation
  function downloadCSV() {
    if (!selectedEmployee) return;
    // Columns: Date, Month, Status, Check-In, Check-Out, Duration
    const columns = ["Date", "Month", "Status", "Check-In", "Check-Out", "Duration"];
    let csv = columns.join(",") + "\n";
    filteredAttendance.forEach((rec) => {
      csv += [
        rec.date,
        rec.month,
        rec.status,
        rec.checkIn,
        rec.checkOut,
        rec.duration || calculateDuration(rec.checkIn, rec.checkOut)
      ].map(x => `"${String(x || "").replace(/"/g, '""')}"`).join(",") + "\n";
    });
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedEmployee.replace(/\s+/g, "_")}_attendance.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 100);
  }

 if (loading)
  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "fixed",
        top: 0,
        left: 0,
        background: "rgba(0,0,0,0.0)", // transparent or change color if needed
        zIndex: 9999
      }}
    >
      <ThreeDot color="#ffffffff" size="medium" text="" textColor="" />
    </div>
  );

  if (error) return <div>{error}</div>;

  return (
    <>
      <div className="adminrecord-main-container container-fluid">
        <section className="adminrecord-table-section col-12 col-lg-8">
          <h2 className="adminrecord-title">All Employee Attendance Records</h2>
          <div className="filter-employee row gx-2" style={{ alignItems: "center" }}>
            <div className="col-12 col-md-auto mb-2">
              <label><b>Filter by Employee:</b></label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="filter-btn ms-2"
              >
                <option value="">--All Employees--</option>
                {employeeNames.map((name, i) =>
                  <option key={name || i} value={name}>{name}</option>
                )}
              </select>
            </div>
            <div className="col-12 col-md-auto mb-2">
              <label><b>Filter by Month:</b></label>
              <select
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                className="filter-btn ms-2"
              >
                {MONTH_OPTIONS.map((m) => (
                  <option key={m.label} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-auto mb-2">
              <label><b>Year:</b></label>
              <input
                type="number"
                value={selectedYear}
                onChange={e => setSelectedYear(Number(e.target.value))}
                className="filter-btn ms-2"
                style={{ width: 78 }}
              />
            </div>
            {/* Refresh button, always shown */}
            <div className="col-12 col-md-auto mb-2">
              <button
                className="refresh-btn"
                
                title="Refresh records"
                onClick={() => setReload(r => r + 1)}
              >🗘 Reload </button>
            </div>
            {/* Download CSV arrow, only when filter is single employee */}
            {selectedEmployee && (
              <div className="col-12 col-md-auto mb-2">
                <button
                  className="download-btn"
                  title="Download attendance as CSV"
                  onClick={downloadCSV}
                
                >
                  ↓
                </button>
              </div>
            )}
          </div>

          <div className="adminrecord-table-container mb-0">
            <table className="adminrecord-table table-responsive">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Month</th>
                  <th>Status</th>
                  <th>Check-In</th>
                  <th>Check-Out</th>
                  <th>Duration</th>
                  <th>Employee</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttendance.length === 0 ? (
                  <tr>
                    <td colSpan="8">No attendance records</td>
                  </tr>
                ) : (
                  filteredAttendance.map((rec, idx) => (
                    <motion.tr
  key={`${rec.employeeName}-${rec.date}-${idx}`}
  initial={{ opacity: 0, y: -20, scale: 0.95 }}
  animate={{ opacity: 1, y: 0, scale: 1 }}
  transition={{
    duration: 0.35,
    ease: "easeOut",
    delay: idx * 0.05   // stagger animation
  }}
  style={{
    marginBottom: "10px"
  }}
>

                      <td>{rec.date}</td>
                      <td>{rec.month}</td>
                      <td>
                        {editingIdx === idx ? (
                          <select
                            value={editStatus}
                            onChange={e => setEditStatus(e.target.value)}
                            style={{ minWidth: 80 }}
                          >
                            {STATUS_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        ) : (
                          rec.status
                        )}
                      </td>
                      <td>
                        {editingIdx === idx ? (
                          <input
                            type="text"
                            value={editCheckIn}
                            onChange={e => setEditCheckIn(e.target.value)}
                            placeholder="hh:mm AM/PM"
                          />
                        ) : (
                          rec.checkIn || "-"
                        )}
                      </td>
                      <td>
                        {editingIdx === idx ? (
                          <input
                            type="text"
                            value={editCheckOut}
                            onChange={e => setEditCheckOut(e.target.value)}
                            placeholder="hh:mm AM/PM"
                          />
                        ) : (
                          rec.checkOut || "-"
                        )}
                      </td>
                      <td>
                        {editingIdx === idx
                          ? calculateDuration(editCheckIn, editCheckOut)
                          : rec.duration || calculateDuration(rec.checkIn, rec.checkOut) || "-"}
                      </td>
                      <td>{rec.employeeName}</td>
                      <td>
                        {editingIdx === idx ? (
                          <>
                            <button
                              disabled={saving}
                              onClick={() => handleSave(idx, rec)}
                              className="save-btn"
                            >
                              {saving ? "Saving..." : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingIdx(null)}
                              className="cancel-btn"
                              style={{ marginLeft: 5 }}
                            >Cancel</button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEdit(idx, rec)}
                              className="edit-btn"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(idx, rec)}
                              className="delete-btn"
                              style={{ marginLeft: 5 }}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </motion.tr>

                  ))
                )}
              </tbody>
              
            </table>
          </div>
        </section>
        {/* Sidebar with leave and badges */}
        <section className="leave-record-sidebar col-12 col-lg-4">
          {selectedEmployee ? (
            <>
              <h3 style={{ textAlign: "center" }}>
                {selectedEmployee}'s Leave Records
              </h3>
              {/* Month badges */}
              <div style={{
                display: "flex",
                gap: "7px",
                flexWrap: "wrap",
                margin: "8px 0 10px"
              }}>
                <span style={{
                  background: "#cfe3ff",
                  color: "#000",
                  borderRadius: "12px",
                  padding: "3px 10px",
                  fontSize: "12px"
                }}>
                  This month Checkout: {monthBadges.checkouts}
                </span>
                <span style={{
                  background: "#ffe9b3",
                  color: "#000",
                  borderRadius: "12px",
                  padding: "3px 10px",
                  fontSize: "12px"
                }}>
                  This month Short: {monthBadges.short}
                </span>
                <span style={{
                  background: "#ffd0d0",
                  color: "#000",
                  borderRadius: "12px",
                  padding: "3px 10px",
                  fontSize: "12px"
                }}>
                  This month Nocheckout: {monthBadges.nocheckout}
                </span>
                <span style={{
                  background: "#ffb0b0",
                  color: "#000",
                  borderRadius: "12px",
                  padding: "3px 10px",
                  fontSize: "12px"
                }}>
                  This month No‑show: {monthBadges.noshow}
                </span>
              </div>
              {/* Leave Table */}
              <table style={{
                width: "100%",
                fontSize: "12px",
                background: "none"
              }}>
                <thead>
                  <tr>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Leave Type</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(filteredLeave) && filteredLeave.length > 0 ? (
                    filteredLeave.map((leave, i) => (
                      <tr key={`${leave.startDate}-${leave.endDate}-${i}`}>
                        <td>{leave.startDate}</td>
                        <td>{leave.endDate}</td>
                        <td>{leave.leaveType}</td>
                        <td>{leave.status}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4">No leave records</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          ) : (
            <div style={{ minHeight: "180px" }}></div>
          )}
        </section>
      </div>
    </>
  );
}

export default AdminAttendance;
